import React, { useState } from "react"
import { Modal, Radio, Input, Typography } from "antd"
import DataQButton from "shared/ui/Buttons/DataQButton.component"
import { notify } from "shared/ui/Notifications/notify"
import {
  useCreateProjectFolderMutation,
  useSetProjectFolderMutation,
} from "features/Projects/api/useProjectsMutations"
import { ProjectData } from "types/project.types"

interface MoveToFolderModalProps {
  open: boolean
  onClose: () => void
  project: ProjectData | null
  existingFolders: string[]
}

const NEW_FOLDER_SENTINEL = "__new__"
const UNGROUPED_SENTINEL = "__ungrouped__"

const MoveToFolderModal: React.FC<MoveToFolderModalProps> = ({
  open,
  onClose,
  project,
  existingFolders,
}) => {
  const { Title } = Typography
  const createFolderMutation = useCreateProjectFolderMutation()
  const setFolderMutation = useSetProjectFolderMutation()

  const initialChoice = project?.metadata?.Folder ?? UNGROUPED_SENTINEL
  const [choice, setChoice] = useState<string>(initialChoice)
  const [newFolderName, setNewFolderName] = useState<string>("")

  // Reset when the modal is re-opened for a different project.
  React.useEffect(() => {
    setChoice(project?.metadata?.Folder ?? UNGROUPED_SENTINEL)
    setNewFolderName("")
  }, [project])

  const handleSubmit = async () => {
    if (!project) return
    let target = ""
    if (choice === UNGROUPED_SENTINEL) {
      target = ""
    } else if (choice === NEW_FOLDER_SENTINEL) {
      const trimmed = newFolderName.trim()
      if (!trimmed) {
        notify.error(
          "Folder name required",
          "Please enter a name for the new folder."
        )
        return
      }
      if (trimmed.includes(".")) {
        notify.error(
          "Invalid folder name",
          "Folder names cannot contain '.'. Pick an existing folder to nest inside."
        )
        return
      }
      target = trimmed
      try {
        await createFolderMutation.mutateAsync(target)
      } catch (error) {
        const message = error instanceof Error ? error.message : "Unknown error"
        notify.error("Error creating folder", message)
        return
      }
    } else {
      target = choice
    }

    try {
      await setFolderMutation.mutateAsync({
        id: project.id,
        owner: project.owner,
        folder: target,
      })
      notify.success(
        "Project moved",
        target ? `Moved to '${target}'.` : "Moved to ungrouped."
      )
      onClose()
    } catch (error) {
      const message = error instanceof Error ? error.message : "Unknown error"
      notify.error("Error moving project", message)
    }
  }

  return (
    <Modal
      open={open}
      centered
      footer={false}
      closable={false}
      destroyOnHidden
      onCancel={onClose}
      className="move-to-folder-modal"
    >
      <Title level={4} style={{ marginTop: "0.5em" }}>
        Move {project?.metadata?.Name ?? "Project"}
      </Title>
      <div className="flex flex-col mt-3">
        <Radio.Group
          value={choice}
          onChange={(e) => setChoice(e.target.value)}
          className="flex flex-col w-full max-h-80 overflow-auto rounded border border-gray-200 divide-y divide-gray-100"
        >
          <Radio
            value={UNGROUPED_SENTINEL}
            className="px-3 py-2 m-0 hover:bg-gray-50"
          >
            <span className="text-gray-700">Ungrouped</span>
          </Radio>
          {existingFolders.map((f) => (
            <Radio key={f} value={f} className="px-3 py-2 m-0 hover:bg-gray-50">
              {f}
            </Radio>
          ))}
          <Radio
            value={NEW_FOLDER_SENTINEL}
            className="px-3 py-2 m-0 hover:bg-gray-50"
          >
            <span className="text-[#36c3ed] font-medium">New folder…</span>
          </Radio>
        </Radio.Group>
        {choice === NEW_FOLDER_SENTINEL && (
          <Input
            value={newFolderName}
            onChange={(e) => setNewFolderName(e.target.value)}
            placeholder="New folder name"
            autoFocus
            className="mt-3"
          />
        )}
      </div>
      <div className="flex justify-end space-x-2 mt-4">
        <DataQButton type="secondary" onClick={onClose}>
          Cancel
        </DataQButton>
        <DataQButton
          type="primary"
          onClick={handleSubmit}
          isDisabled={setFolderMutation.isPending}
        >
          Move
        </DataQButton>
      </div>
    </Modal>
  )
}

export default MoveToFolderModal
